import { t } from '@/utils/translation';
import { Route, Location } from 'vue-router/types/router';
import { keycloak, keycloakB2B, keycloakB2C } from '@/services/auth/auth.service';
import store from '@/store';
import { parseParams } from '@/utils/strings';
import { sentry } from '@/utils/sentry';
import { isAccessible } from '@/utils/permissions';
import { loadHotJar } from '@/utils/load-hot-jar';
import { loadUserCentrics, initUsercentricsConsentListeners } from '@/utils/usercentrics';
import { UserState } from '@/store/user/user.model';
import { CompanyType } from '@/pages/driver-management/driver-management.model';

type RouteMeta = { requiresMigration: boolean; requiresAuth: boolean; requiresCustomerAuth: boolean; requiresB2BAuth: boolean; disabled: boolean };

let keycloakInstance = keycloak;
const setUserContext = () => {
  const {
    user: { uuid, language },
    roles,
  } = store.state.users;

  sentry.setUser({
    id: uuid,
    roles: roles,
    language,
  });
};

const replaceUrl = (url: string) => window.location.replace(url);
const loadUserInformation = async (meta: RouteMeta) => {
  if (meta.requiresB2BAuth || store.getters['users/useB2BAuthClient']) {
    await store.dispatch('users/loadB2BUserInformation');
  } else {
    await store.dispatch('users/loadUserInformation');
  }
};

const bootstrapMyAccount = async (to: Route, _: Route, next: (to?: Location) => void, meta: RouteMeta) => {
  if (!store.state.users.user.uuid) {
    await loadUserInformation(meta);
  }
  const { language, country, legalEntityId } = store.state.users.user;

  loadUserCentrics();
  loadHotJar(legalEntityId);
  initUsercentricsConsentListeners();
  setUserContext();

  const localeString = `${language}-${country}`;
  store.dispatch('app/loadTranslations', {
    localeString,
  });

  const query = parseParams(window.location.search);
  let location: Location | undefined;
  const { roles } = store.state.users;

  const isValidAdmin = Array.isArray(roles) && roles.length > 0;
  if (isValidAdmin && !meta.requiresB2BAuth) {
    // Only load data when admin is valid
    if (isAccessible('Drivers', roles) && store.state.driverManagement.pendingTotal < 0) {
      store.dispatch('driverManagement/getPendingTotal');
    }

    await store.dispatch('companies/getMyCompany');
    if (store.state.companies.company.paymentProfileType === CompanyType.CENTRAL) {
      await store.dispatch('rentals/loadCompanyPaymentProfiles');
      await store.dispatch('rentals/loadCompanyPerformingState');
    }
  }

  switch (true) {
    // Check if the current user is being invited
    case query.action === 'codriver':
      location = { name: 'co-driver' };
      break;
    case query.action === 'request':
      location = { name: 'driver-request' };
      break;
    // Check that the current user is a Business Admin user
    case !isValidAdmin:
      location = { name: 'not-a-business-customer-page' };
      break;
    case !isAccessible(to.name!, roles):
      location = { name: to.name === 'Dashboard' ? 'Account' : 'forbidden' };
      break;
    default:
      break;
  }

  await store.dispatch('app/setAppReady', true);
  if (to.matched.some((record) => record.meta.requiresCredits)) {
    if (store.state.credits.creditsAllowed) {
      next(location);
    } else {
      return store.dispatch('app/showError', {
        title: `CREDITS: ${t('error_msg_server')}`,
        description: t('error_msg_server_description'),
        onClose: () => replaceUrl('/'),
        err: new Error('Credits are not allowed'),
      });
    }
  } else {
    next(location);
  }
};

const authenticationHandler = async (to: Route, _: Route, next: (to?: Location) => void, meta: RouteMeta) => {
  const myaccountURL = './';
  const { requiresMigration, requiresCustomerAuth, requiresB2BAuth } = meta;
  if (requiresMigration || requiresCustomerAuth) {
    keycloakInstance = keycloakB2C;
  } else if (requiresB2BAuth || store.getters['users/useB2BAuthClient']) {
    keycloakInstance = keycloakB2B;
  }

  await keycloakInstance.init('query', 'login-required');

  keycloakInstance.onAuthError(async () => {
    await keycloakInstance.logout();
  });

  if (!keycloakInstance.isAuthenticated) {
    if (requiresMigration) {
      keycloakInstance.login('dn');
    }
  }

  try {
    if (requiresMigration || requiresCustomerAuth) {
      await store.dispatch('users/loadUserInformation');
      const { language, country } = store.state.users.user;
      const { legalEntityId } = store.state.users.user as UserState;

      loadUserCentrics();
      loadHotJar(legalEntityId);
      initUsercentricsConsentListeners();

      await store.dispatch('app/loadTranslations', {
        localeString: `${language}_${country}`,
      });
      const query = parseParams(window.location.search);
      switch (query.action) {
        case 'codriver':
          return window.location.replace(`./#/co-driver${window.location.search}`);
        case 'request':
          return window.location.replace(`./#/driver-request${window.location.search}`);
      }

      await store.dispatch('app/setAppReady', true);
      next();
    } else {
      await bootstrapMyAccount(to, _, next, meta);
    }
  } catch (e) {
    const err: any = e;
    const ERROR_CODE = err && err.response ? err.response.status : 0;

    switch (ERROR_CODE) {
      case 401:
        if (requiresMigration) {
          keycloakInstance.login('dn');
        } else {
          await keycloakInstance.init('query', 'login-required');
        }
        break;
      case 400:
      case 500:
      case 503:
      case 504:
        store.dispatch('app/showError', {
          title: t('error_msg_server'),
          description: t('error_msg_server_description'),
          onClose: () => replaceUrl(myaccountURL),
          err,
        });
        break;
      default:
        store.dispatch('app/showError', {
          title: t('error_msg_server'),
          description: t('error_msg_unknown'),
          onClose: () => keycloakInstance.logout(),
          err,
        });
        break;
    }
  }
};

export const defaultRouteHandler = async (to: Route, from: Route, next: (to?: Location) => void) => {
  const isWebView = to.matched.some((record) => record.meta.isWebView);
  await store.dispatch('app/toggleWebView', isWebView);
  const meta = to.meta as RouteMeta;
  // ToDo : Clean this up after b2b takes ownership of email handling
  /* current link sent by cuba in invitation email doesn't contain proper route.
  Invalid Example:/business/?action=codriver&validationCode=fid1uRypYWn7eQJ2VEVnIqgi
  Valid Example:/business/#/co-driver?action=codriver&validationCode=fid1uRypYWn7eQJ2VEVnIqgi
  this following part handles it as a work around */
  const params = new URLSearchParams(window.location.search);
  const action = params.get('action');

  if (action && /codriver|request/.test(action)) {
    Object.assign(meta, { requiresCustomerAuth: true, requiresAuth: undefined });
  }

  if (to.matched.some(({ meta: { disabled } }) => disabled)) {
    next({ path: '/' });
  }

  if (
    to.matched.some(
      ({ meta: { requiresMigration, requiresAuth, requiresCustomerAuth, requiresB2BAuth } }) => requiresAuth || requiresMigration || requiresCustomerAuth || requiresB2BAuth,
    )
  ) {
    await authenticationHandler(to, from, next, meta);
  } else {
    try {
      await store.dispatch('app/loadLocaleFromUrl', to.query);
      await store.dispatch('app/setAppReady', true);
    } catch (err) {
      store.dispatch('app/showError', {
        title: t('error_msg_translations'),
        description: t('error_msg_translations_description'),
        err,
      });
    }
    next();
  }
};
